import React, { useState, useEffect } from 'react'
import { APP_CONFIG } from '../../config'

/**
 * Public Terms & Conditions viewer page.
 * Opens in a new browser tab (no authentication required).
 * Fetches the latest published T&C for the requested language.
 *
 * URL: /terms?lang=en-GB  (defaults to en-GB if no lang param)
 */
const TermsPage = () => {
  const [terms, setTerms] = useState(null)
  const [languages, setLanguages] = useState([])
  const [selectedLang, setSelectedLang] = useState('en-GB')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Read initial language from URL query parameter
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const lang = params.get('lang')
    if (lang) {
      setSelectedLang(lang)
    }
  }, [])

  // Fetch available languages on mount
  useEffect(() => {
    fetch(`${APP_CONFIG.baseURL}terms/languages`)
      .then(res => res.json())
      .then(data => {
        if (data.languages) {
          setLanguages(data.languages)
        }
      })
      .catch(() => {
        // Silently fail — language selector just won't show
      })
  }, [])

  // Fetch T&C content when language changes
  useEffect(() => {
    setLoading(true)
    setError(null)

    fetch(`${APP_CONFIG.baseURL}terms/latest?languageCode=${encodeURIComponent(selectedLang)}`)
      .then(res => {
        if (!res.ok) throw new Error('Not found')
        return res.json()
      })
      .then(data => {
        setTerms(data)
        setLoading(false)
      })
      .catch(() => {
        setError('No published Terms and Conditions found for this language. Please contact the administrator to publish a Terms and Conditions version.')
        setTerms(null)
        setLoading(false)
      })
  }, [selectedLang])

  const handleLanguageChange = (e) => {
    const lang = e.target.value
    setSelectedLang(lang)
    // Update URL without reload
    const url = new URL(window.location)
    url.searchParams.set('lang', lang)
    window.history.replaceState({}, '', url)
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header */}
        <header style={styles.header}>
          <div style={styles.headerContent}>
            <h1 style={styles.logo}>TREND Networks</h1>
            {languages.length > 1 && (
              <select
                style={styles.langSelect}
                value={selectedLang}
                onChange={handleLanguageChange}
              >
                {languages.map(lang => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </select>
            )}
          </div>
        </header>

        {/* Content */}
        <main style={styles.main}>
          {loading && (
            <div style={styles.centered}>
              <p style={styles.loadingText}>Loading terms and conditions...</p>
            </div>
          )}

          {error && !loading && (
            <div style={styles.errorBox}>
              <p>{error}</p>
            </div>
          )}

          {terms && !loading && (
            <>
              <h1 style={styles.title}>{terms.title}</h1>
              <div style={styles.meta}>
                <span>Version {terms.version}</span>
                {terms.publishedAt && (
                  <span> · Published {new Date(terms.publishedAt).toLocaleDateString()}</span>
                )}
              </div>
              <div
                style={styles.content}
                dangerouslySetInnerHTML={{ __html: terms.content }}
              />
            </>
          )}
        </main>

        {/* Footer */}
        <footer style={styles.footer}>
          <p>© {new Date().getFullYear()} TREND Networks. All rights reserved.</p>
        </footer>
      </div>
    </div>
  )
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f5f7fa',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
  },
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '0 24px'
  },
  header: {
    padding: '20px 0',
    borderBottom: '1px solid #e0e0e0',
    marginBottom: '32px'
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  logo: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 700,
    color: '#005795'
  },
  langSelect: {
    padding: '6px 12px',
    border: '1px solid #d0d0d0',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: '#fff',
    cursor: 'pointer'
  },
  main: {
    minHeight: '60vh',
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '40px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    marginBottom: '32px'
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#333',
    margin: '0 0 12px 0',
    borderBottom: '2px solid #005795',
    paddingBottom: '12px'
  },
  meta: {
    color: '#666',
    fontSize: '13px',
    marginBottom: '28px'
  },
  content: {
    lineHeight: 1.7,
    color: '#444',
    fontSize: '15px'
  },
  centered: {
    textAlign: 'center',
    padding: '60px 0'
  },
  loadingText: {
    color: '#666',
    fontSize: '16px'
  },
  errorBox: {
    backgroundColor: '#ffebee',
    borderLeft: '4px solid #f44336',
    padding: '16px 20px',
    color: '#c62828',
    borderRadius: '4px'
  },
  footer: {
    textAlign: 'center',
    padding: '20px 0',
    borderTop: '1px solid #e0e0e0',
    color: '#999',
    fontSize: '13px'
  }
}

export default TermsPage
